import { defineStore, storeToRefs } from 'pinia'
import { ref, onMounted, onUnmounted, watch } from 'vue'

// Types
import type { Ref } from 'vue'
import type { AudioPlayer, Track, AudioLoadError } from '@/customTypes'
import { HowlErrorCode } from '@/customTypes'
import type { ManagedTimeout } from '@/customTypes'

// Stores
import { useMediaDevicesStore } from './MediaDevicesStore'
import { useAudioAnalyserStore } from './AudioAnalyserStore'
import { useDataStore } from '@/stores/DataStore'
import { usePreloadStore } from './PreloadStore'

// Services
import { Howl, Howler } from 'howler'
import Logger from '@/services/Logger.js'

// Helpers
import { useTimeout } from '@/utils/helpers'

declare module 'howler' {
  interface Howl {
    _sounds: Array<{
      _node: AudioNode
    }>
  }
}

interface LoadErrorTimeout {
  timeout: ManagedTimeout
  retryCount: number
}

export const useSoundEffectsStore = defineStore('audio', () => {
  const logger: any = new Logger('SoundEffectsStore', true)
  const tracks = ref<Track[]>([])
  const mediaDevicesStore = useMediaDevicesStore()
  const { selectedAudioOutputDeviceId } = storeToRefs(mediaDevicesStore)
  const audioAnalyserStore = useAudioAnalyserStore()
  const dataStore = useDataStore()
  const preloadStore = usePreloadStore()
  const { setManagedTimeout, clearManagedTimeout } = useTimeout()
  let resettingAudioSystem = false

  const fadeDuration: Ref<number> = ref(5) // in seconds
  const muted: Ref<boolean> = ref(false)
  const audioContext = ref<AudioContext | null>(null)
  const preferredOutputDeviceId = ref<string>('')

  const loadErrorTimeouts = ref<Map<string, LoadErrorTimeout>>(new Map())
  const pendingTrackPromises = new Map<string, Promise<AudioPlayer>>()

  const maxRetries = 3
  // In seconds
  const retryDelayTime = 3
  const errorTimeoutTime = 10

  onMounted(async () => {
    if (mediaDevicesStore.outputSelectionSupported) {
      let { audioOutputId = '' } = mediaDevicesStore.getStoredAudioDevices()
      const isAvailable = await mediaDevicesStore.checkMediaDeviceAvailability(
        audioOutputId,
        'audiooutput'
      )
      if (!isAvailable) {
        audioOutputId = ''
      }
      await updateOutputDevice(audioOutputId)
    }
  })

  onUnmounted(() => {
    logger.info('SoundEffectsStore unmounted')
    stopTracks()
    clearAllLoadErrorTimeouts()
    unmountHowler()
  })

  const startLoadErrorTimeout = (error: HowlErrorCode, id: string) => {
    const existingTimeout = loadErrorTimeouts.value.get(id)
    if (existingTimeout?.retryCount !== maxRetries) {
      return
    }

    logHowlerError(error, id)

    const timeout = setManagedTimeout(() => {
      dataStore.setScreenerError(
        'Audio Track Error',
        'Something went wrong when loading screener assets. Please check your internet connection and try reloading the page.'
      )
      logger.error(`Error loading audio timed out for ID ${id}, showing error view`)
      loadErrorTimeouts.value.delete(id)
    }, errorTimeoutTime * 1000)

    loadErrorTimeouts.value.set(id, {
      timeout,
      retryCount: (existingTimeout?.retryCount || maxRetries) + 1
    })
  }

  const clearLoadErrorTimeout = (id: string) => {
    const timeoutEntry = loadErrorTimeouts.value.get(id)
    if (timeoutEntry) {
      clearManagedTimeout(ref(timeoutEntry.timeout))
      loadErrorTimeouts.value.delete(id)
    }
  }

  const clearAllLoadErrorTimeouts = () => {
    loadErrorTimeouts.value.forEach((entry) => {
      clearManagedTimeout(ref(entry.timeout))
    })
    loadErrorTimeouts.value.clear()
  }

  const logHowlerError = (error: HowlErrorCode, id: string) => {
    switch (error) {
      case HowlErrorCode.LoadFailed:
        logger.error('Load failed for sound with id:', id)
        break
      case HowlErrorCode.DecodeFailed:
        logger.error('Decode failed for sound with id:', id)
        break
      case HowlErrorCode.NoSupport:
        logger.error('No support for sound format with id:', id)
        break
      default:
        logger.error('Unknown error with id:', id, 'Error code:', error)
    }
  }

  /**
   * Creates an audio player for a given track and loads the track.
   * @param {Track} track - The track object containing track details.
   * @returns {Promise<AudioPlayer>} - A promise that resolves with the created AudioPlayer.
   */
  const createPlayerForTrack = async (track: Track, retryCount = 0): Promise<AudioPlayer> => {
    return new Promise((resolve, reject) => {
      const existingTrack = tracks.value.find((t) => t.id === track.id)
      if (existingTrack) return resolve(existingTrack.howl as AudioPlayer)

      // Track volume set to 0 for fade in effect when playing
      track.howl = new Howl({
        src: [track.url],
        html5: false,
        volume: 0,
        loop: track.loop,
        onload: () => {
          const timeout = loadErrorTimeouts.value.get(track.id)
          if (timeout) {
            clearLoadErrorTimeout(track.id)
          }
          tracks.value.push(track)
          logger.info('Added new track', track.id)
          resolve(track.howl as AudioPlayer)
        },
        onloaderror: async (id, error) => {
          logger.warn('Error loading audio:', error)
          if (retryCount < maxRetries) {
            logger.info(`Retrying to load audio (${retryCount + 1}/${maxRetries})...`)

            const retryDelay = retryDelayTime * 1000 * (retryCount + 1)
            const timeout = setManagedTimeout(() => {
              createPlayerForTrack(track, retryCount + 1)
                .then((resolvedPlayer) => {
                  loadErrorTimeouts.value.delete(track.id)
                  resolve(resolvedPlayer)
                })
                .catch(reject)
            }, retryDelay)

            loadErrorTimeouts.value.set(track.id, {
              timeout,
              retryCount: retryCount + 1
            })
          } else {
            const loadError: AudioLoadError = {
              name: 'AudioLoadError',
              message: `Failed to load audio after ${maxRetries} attempts`,
              id: track.id,
              error: error as HowlErrorCode,
              stack: new Error().stack
            }

            reject(loadError)
          }
        },
        onplay: () => {
          if (!audioContext.value) audioContext.value = Howler.ctx
          audioAnalyserStore.handleTrackPlaybackChange(track.id, true)
        },
        onend: () => {
          logger.info('Track finished playing', track.id)
          audioAnalyserStore.handleTrackPlaybackChange(track.id, false)
        },
        onstop: () => {
          logger.info('Track stopped', track.id)
          audioAnalyserStore.handleTrackPlaybackChange(track.id, false)
        },
        onplayerror: (id, error) => {
          logger.error('Play error for sound with id', track.id, { howlId: id, error })
          resetAudioSystemAfterPlaybackError('onplayerror', error)
        }
      }) as AudioPlayer

      track.howl.customVolume = track.volume
      track.howl.id = track.id
      track.howl.start = (fade?: boolean | number) => playTrack(track.howl as AudioPlayer, fade)
      track.howl.end = (fade?: boolean) => stopTrack(track.howl as AudioPlayer, fade)
      track.howl.remove = () => removeTrack(track.howl as AudioPlayer)
      track.howl.setVolume = (level: number, fade?: boolean) =>
        adjustVolume(track.howl as AudioPlayer, level, fade)
      track.howl.connectAnalyzer = (analyser: AnalyserNode) =>
        createAudioAnalyser(analyser, track.howl as AudioPlayer)

      if (!audioContext.value && Howler.ctx) {
        audioContext.value = Howler.ctx
      }
    })
  }

  /**
   * Retrieves the audio player for a given track reference.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   * @returns {AudioPlayer | undefined} - The corresponding AudioPlayer or undefined if not found.
   */
  const getAudioPlayer = async (
    trackReference: AudioPlayer | string
  ): Promise<AudioPlayer | undefined> => {
    if (typeof trackReference === 'string') {
      const track = tracks.value.find((t) => t.id === trackReference)?.howl as AudioPlayer
      if (track) {
        return track
      } else {
        const errorAssetTrack = preloadStore.getFailedAsset(trackReference) as Track | undefined
        if (errorAssetTrack) {
          return await createPlayerForTrack(errorAssetTrack)
        }
        return undefined
      }
    }
    return trackReference
  }

  /**
   * Plays a given track, with an optional fade-in effect.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   * @param {boolean | number} [fade=false] - Whether to apply a fade-in effect, and its duration if specified.
   */
  const playTrack = async (
    trackReference: AudioPlayer | string,
    fade: boolean | number = false
  ): Promise<void> => {
    const track = await getAudioPlayer(trackReference)

    if (track) {
      logger.log('Attempting to play track', track.id)
      // Ensure the track starts at the beginning
      track.seek(0)
      let setVolume = 0
      if (!muted.value) {
        setVolume = track.customVolume > 1 ? 1 : track.customVolume
      }
      if (fade && !muted.value) {
        track.volume(0)
        const fadeVolume = typeof fade === 'number' ? fade : setVolume
        track.fade(0, fadeVolume, fadeDuration.value * 1000)
      } else {
        track.volume(setVolume)
      }

      track.once('playerror', (_, error) => {
        logger.error('Play error during playback attempt', { trackId: track.id, error })
        resetAudioSystemAfterPlaybackError('playTrack', error)
      })

      try {
        track.play()
      } catch (error) {
        logger.error('Synchronous play error encountered', { trackId: track.id, error })
        resetAudioSystemAfterPlaybackError('playTrack-catch', error)
        throw error
      }
    } else {
      logger.error(`Track with id ${trackReference} not found or not loaded`)
    }
  }

  /**
   * Stops a given track, with an optional fade-out effect.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   * @param {boolean} [fade=false] - Whether to apply a fade-out effect.
   */
  const stopTrack = async (
    trackReference: AudioPlayer | string,
    fade: boolean = false
  ): Promise<void> => {
    const track = await getAudioPlayer(trackReference)
    if (track) {
      if (!track.playing()) return
      logger.info('Stopping track', track.id)
      if (fade) {
        track.fade(track.volume(), 0, (fadeDuration.value / 2) * 1000)
        setManagedTimeout(() => track.stop(), (fadeDuration.value / 2) * 1000)
      } else {
        track.stop()
      }
    }
  }

  /**
   * Removes a given track.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   */
  const removeTrack = async (trackReference: AudioPlayer | string): Promise<void> => {
    const track = await getAudioPlayer(trackReference)
    if (track) {
      const trackId = track.id
      const trackIndex = tracks.value.findIndex((t) => t.id === trackId)

      if (trackIndex !== -1) {
        if (track.playing()) track.stop()
        track.off()
        track.unload()

        if (tracks.value[trackIndex]) tracks.value[trackIndex].howl = undefined
        tracks.value.splice(trackIndex, 1)
        logger.info(`Removed track with id ${trackId}`)
      } else {
        logger.warn(`Track with id ${trackId} not found for removal`)
      }
    } else {
      logger.warn(`Track with id ${trackReference} not found for removal`)
    }
  }

  /**
   * Adjusts the volume of a given track, with an optional fade effect.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   * @param {number} volume - The volume level to set.
   * @param {boolean} [fade=false] - Whether to apply a fade effect.
   */
  const adjustVolume = async (
    trackReference: AudioPlayer | string,
    volume: number,
    fade: boolean = false
  ): Promise<void> => {
    const track = await getAudioPlayer(trackReference)

    if (track) {
      logger.info('Adjusting volume for track', track.id)
      track.customVolume = volume // Update custom volume
      if (fade) {
        track.fade(track.volume(), volume, fadeDuration.value * 1000)
      } else {
        track.volume(volume)
      }
    } else {
      logger.error(`Track with id ${trackReference} not found`)
    }
  }

  /**
   * Creates an audio analyser node for a given track.
   * @param {AnalyserNode} analyser - The analyser node to connect.
   * @param {AudioPlayer | string} trackReference - The track reference, either an AudioPlayer or track ID.
   * @returns {AnalyserNode | undefined} - The connected analyser node or undefined if the track or audio context is not found.
   */
  const createAudioAnalyser = async (
    analyser: AnalyserNode,
    trackReference: AudioPlayer | string
  ): Promise<AnalyserNode | undefined> => {
    if (!audioContext.value) audioContext.value = Howler.ctx
    const context = audioContext.value
    if (!context) return
    const track = await getAudioPlayer(trackReference)
    if (!track) {
      logger.error('No track found for audio analyser')
      return
    }

    let analyserConnected = false

    const attachNodes = () => {
      const activeSoundNodes =
        track._sounds?.map((sound) => sound?._node).filter((node): node is AudioNode => !!node) ??
        []

      if (!activeSoundNodes.length) {
        return false
      }

      activeSoundNodes.forEach((soundNode) => {
        try {
          soundNode.connect(analyser)
        } catch (error) {
          logger.error('Failed to connect sound node to analyser', { error, trackId: track.id })
        }
      })

      if (!analyserConnected) {
        try {
          analyser.connect(context.destination)
          analyserConnected = true
        } catch (error) {
          logger.error('Failed to connect analyser to destination', { error, trackId: track.id })
        }
      }
      return true
    }

    if (!attachNodes()) {
      logger.info('No active sound nodes yet; awaiting track playback before attaching analyser', {
        trackId: track.id
      })

      track.once('play', () => {
        if (!audioContext.value) return
        if (!attachNodes()) {
          logger.warn('Failed to attach analyser after playback started', { trackId: track.id })
        }
      })
    }

    return analyser
  }

  /**
   * Stops currently playing tracks.
   */
  const stopTracks = (): void => {
    logger.info('Stopping all tracks')
    tracks.value.forEach((track) => track.howl?.stop())
  }

  /*
   * resets howler and audio context
   */
  const unmountHowler = () => {
    tracks.value = []
    Howler.stop()
    Howler.unload()
    if (audioContext.value && audioContext.value.state !== 'closed') {
      audioContext.value?.close()
    }
    audioContext.value = null
  }

  /**
   * Updates the output device for the audio context on supported browsers.
   * @param {string} deviceId - The ID of the output device to set.
   * @returns {Promise<void>} - A promise that resolves when the output device is updated.
   */
  const applyOutputSinkId = async (context: AudioContext, deviceId: string) => {
    if (!mediaDevicesStore.outputSelectionSupported) return

    if (typeof (context as any).setSinkId !== 'function') {
      logger.warn('AudioContext does not support setSinkId; skipping output selection')
      return
    }

    logger.info('Applying output sink ID to audio context', { deviceId })
    try {
      // @ts-ignore
      await context.setSinkId(deviceId !== 'default' ? deviceId : '')
      const contextWithOnError = context as AudioContext & {
        onerror?: ((event: unknown) => void) | null
      }
      const handleSinkError = (event: unknown) => {
        logger.error('AudioContext sinkId error event received, resetting audio system.', {
          event,
          deviceId
        })
        resetAudioSystemAfterPlaybackError('audioContext-sinkId-error', event)
      }
      if ('onerror' in contextWithOnError) {
        contextWithOnError.onerror = handleSinkError
      } else if (typeof context.addEventListener === 'function') {
        context.addEventListener('error' as any, handleSinkError as EventListener)
      }
    } catch (error) {
      logger.log('Failed to set sinkId, resetting audio system.', { error, deviceId })
      resetAudioSystemAfterPlaybackError('setSinkId-error', error)
    }
  }

  const updateOutputDevice = async (deviceId: string): Promise<void> => {
    preferredOutputDeviceId.value = deviceId

    if (!mediaDevicesStore.outputSelectionSupported) return

    if (!audioContext.value && Howler.ctx) {
      audioContext.value = Howler.ctx
    }

    const context = audioContext.value

    if (!context) {
      logger.info('Audio context not yet available; deferring output sink application', {
        deviceId
      })
      return
    }

    await applyOutputSinkId(context, deviceId)
  }

  watch(
    selectedAudioOutputDeviceId,
    (deviceId) => {
      const nextDeviceId = deviceId || ''
      updateOutputDevice(nextDeviceId).catch((error: unknown) => {
        logger.error('Failed to apply audio output device selection from store', {
          error,
          deviceId: nextDeviceId
        })
      })
    },
    { immediate: true }
  )

  watch(audioContext, (context) => {
    if (!context) return
    const deviceId = preferredOutputDeviceId.value
    if (deviceId === undefined || deviceId === null) return

    applyOutputSinkId(context, deviceId).catch((error: unknown) => {
      logger.error('Failed to apply preferred sink after audio context became available', {
        error,
        deviceId
      })
    })
  })

  /**
   * Creates a video player for a track with a webm source to prevent the device from sleeping.
   * @param {string} src - Base64 encoded mp4 source.
   * @returns {HTMLVideoElement | Error} - The created video element or an error if the audio context is unavailable.
   */
  function createVideoPlayerForNoSleep(src: string): HTMLVideoElement | Error {
    if (!audioContext.value) return new Error('No audio context available')
    const videoElement = document.createElement('video')

    videoElement.src = src
    videoElement.controls = true
    videoElement.autoplay = true
    videoElement.playsInline = true

    const context = audioContext.value

    const gainNode = context.createGain()
    gainNode.gain.value = 1

    const source = context.createMediaElementSource(videoElement)
    source.connect(gainNode)

    videoElement.addEventListener('loadedmetadata', () => {
      if (videoElement.duration <= 1) {
        // webm source
        videoElement.setAttribute('loop', '')
      } else {
        // mp4 source
        videoElement.addEventListener('timeupdate', () => {
          if (videoElement.currentTime > 0.5) {
            videoElement.currentTime = Math.random()
          }
        })
      }
    })

    return videoElement
  }

  const toggleMuted = (force?: boolean): void => {
    if (force !== undefined) {
      muted.value = force
      return
    }

    muted.value = !muted.value

    if (muted.value === false) {
      tracks.value.forEach((track) => {
        adjustVolume(track.id, track.howl!.customVolume)
      })
    }

    if (muted.value === true) {
      tracks.value.forEach((track) => {
        adjustVolume(track.id, 0)
      })
    }
  }

  const resetAudioSystemAfterPlaybackError = (context: string, error: unknown) => {
    if (resettingAudioSystem) {
      logger.info('Playback error reset already in progress, skipping duplicate request', {
        context,
        error
      })
      return
    }

    resettingAudioSystem = true

    try {
      logger.warn('Resetting audio system after playback error', { context, error })

      try {
        audioAnalyserStore.destroyAudioAnalyser()
      } catch (destroyError) {
        logger.error('Failed to destroy audio analyser during playback error reset', destroyError)
      }

      try {
        stopTracks()
      } catch (stopError) {
        logger.error('Failed to stop tracks during playback error reset', stopError)
      }

      try {
        unmountHowler()
      } catch (unmountError) {
        logger.error('Failed to unmount Howler during playback error reset', unmountError)
      }
    } finally {
      resettingAudioSystem = false
    }
  }

  const removeTrackSync = (trackId: string): void => {
    if (!trackId) return
    const trackIndex = tracks.value.findIndex((t) => t.id === trackId)

    if (trackIndex !== -1) {
      const track = tracks.value[trackIndex]
      if (track.howl?.playing()) {
        track.howl.stop()
      }
      track.howl?.off()
      track.howl?.unload()

      tracks.value.splice(trackIndex, 1)
      logger.info(`Synchronously removed track with id ${trackId}`)
    } else {
      logger.warn(`Track with id ${trackId} not found for sync removal`)
    }
  }

  const createTestOutputTrack = async (trackId: string, url: string): Promise<AudioPlayer> => {
    const existingTrack = tracks.value.find((track) => track.id === trackId)?.howl as
      | AudioPlayer
      | undefined
    if (existingTrack) {
      logger.info('Reusing existing test output track', trackId)
      return existingTrack
    }

    const pendingPromise = pendingTrackPromises.get(trackId)
    if (pendingPromise) {
      logger.info('Awaiting pending creation of test output track', trackId)
      return pendingPromise
    }

    logger.info('Creating new test output track', trackId)
    const creationPromise = createPlayerForTrack({
      id: trackId,
      name: 'Audio Output Test',
      url,
      volume: 1,
      loop: true
    })
      .then((player) => {
        pendingTrackPromises.delete(trackId)
        return player
      })
      .catch((error) => {
        pendingTrackPromises.delete(trackId)
        throw error
      })

    pendingTrackPromises.set(trackId, creationPromise)
    return creationPromise
  }

  return {
    tracks,
    audioContext,
    fadeDuration,
    createPlayerForTrack,
    getAudioPlayer,
    muted,
    playTrack,
    stopTrack,
    stopTracks,
    removeTrack,
    adjustVolume,
    updateOutputDevice,
    createVideoPlayerForNoSleep,
    createTestOutputTrack,
    startLoadErrorTimeout,
    clearLoadErrorTimeout,
    toggleMuted,
    unmountHowler,
    removeTrackSync,
    clearAllLoadErrorTimeouts
  }
})
